import { GMAPS_API_KEY } from 'constants/envs'
import { AdvancedMarker, AdvancedMarkerAnchorPoint, APIProvider, Map, MapEvent } from '@vis.gl/react-google-maps'
import { useCallback, useState } from 'react'
import styles from './mapStyles.json'
import NewProducerMarker from './NewProducerMarker'
import { NewPositionsWithProducers, NewProducerMapProps, PopulatedLocation } from './types'

const NewProducerMap = ({
  defaultOpenSlug,
  locations,
  height = '100%',
  zoom = 8,
  center: conditionalCenter,
  enablePanning,
  onBoundsChange,
  fitBounds
}: NewProducerMapProps) => {
  // Constants
  const center = conditionalCenter || {
    lat: 50.943383,
    lng: 3.928225
  }

  // Local state
  const [popupPositionIndex, setPopupPositionIndex] = useState<number | undefined>(undefined)
  const [popupProducerSlug, setPopupProducerSlug] = useState<string | undefined>(defaultOpenSlug)
  const [activePositionIndex, setActivePositionIndex] = useState<number | undefined>(undefined)
  const [activeProducerLocation, setActiveProducerLocation] = useState<string | undefined>(undefined)
  const [centerLastSelectedProd, setCenterLastSelectedProd] = useState<{ lat: number; lng: number } | undefined>(center)

  /**
   * Returns the sorted positions
   *
   * @returns PositionsWithProducers[]
   */
  const getSortedPositions = useCallback((): NewPositionsWithProducers[] => {
    // Filter the producers with positions
    const producersWithPositions = locations.filter((loc) => loc.coordinates)

    // Reduce the producersWithPositions to an array of PositionsWithProducers objects
    const positionsWithProducerInfo = producersWithPositions.reduce((positionsWithProducer, producer, index) => {
      const {
        coordinates: { lat, long }
      } = producer

      positionsWithProducer.push({ index, position: { lat: Number(lat), lng: Number(long) }, producer })

      return positionsWithProducer
    }, [] as NewPositionsWithProducers[])

    // Sort & return the positionsWithproducers
    return positionsWithProducerInfo.slice().sort((a, b) => {
      if (
        (a.producer.slug === popupProducerSlug && a.index === popupPositionIndex) ||
        (a.producer.slug === activeProducerLocation && a.index === activePositionIndex)
      ) {
        return 1
      }

      if (
        (b.producer.slug === popupProducerSlug && b.index === popupPositionIndex) ||
        (b.producer.slug === activeProducerLocation && b.index === activePositionIndex)
      ) {
        return -1
      }

      return 0
    })
  }, [activePositionIndex, activeProducerLocation, locations, popupPositionIndex, popupProducerSlug])

  /**
   * Centers the locations on the map
   *
   * @param {MapEvent} e
   * @param {PopulatedLocation[]} locations
   */
  const centerLocationsOnMap = (e: MapEvent, locations: PopulatedLocation[]) => {
    const mapBounds = e.map.getBounds()

    locations.forEach((location) => {
      mapBounds.extend({ lat: location.coordinates.lat, lng: location.coordinates.long })
    })

    e.map.fitBounds(mapBounds)
  }

  return (
    <div style={{ height, width: '100%' }}>
      <APIProvider apiKey={GMAPS_API_KEY}>
        <Map
          mapId="newProducerMap"
          style={{ width: '100%' }}
          defaultCenter={centerLastSelectedProd}
          defaultZoom={zoom}
          gestureHandling={enablePanning ? 'greedy' : 'cooperative'}
          disableDefaultUI={true}
          scrollwheel={false}
          disableDoubleClickZoom={true}
          zoomControl={true}
          minZoom={8}
          maxZoom={15}
          fullscreenControl={false}
          styles={styles}
          onClick={() => {
            setPopupProducerSlug(undefined)
            setPopupPositionIndex(undefined)
          }}
          onBoundsChanged={(e) => onBoundsChange && onBoundsChange(e.detail.bounds)}
          onTilesLoaded={(e) => fitBounds && centerLocationsOnMap(e, locations)}
        >
          {getSortedPositions().map(({ position, index, producer }) => {
            if (!Number.isNaN(position.lat) && !Number.isNaN(position.lng)) {
              return (
                <AdvancedMarker
                  key={index}
                  position={{ lat: position.lat, lng: position.lng }}
                  anchorPoint={AdvancedMarkerAnchorPoint['BOTTOM']}
                >
                  <NewProducerMarker
                    key={`${producer.id}${index}`}
                    location={producer}
                    lat={position.lat}
                    lng={position.lng}
                    index={index}
                    onMouseEnter={(producer, index) => {
                      setActiveProducerLocation(producer.slug)
                      setActivePositionIndex(index)
                    }}
                    onMouseLeave={() => {
                      setActiveProducerLocation(undefined)
                      setActivePositionIndex(undefined)
                    }}
                    onClick={(producer) => {
                      setCenterLastSelectedProd({ lat: Number(producer.coordinates.lat), lng: Number(producer.coordinates.long) })
                      setPopupProducerSlug(producer.slug)
                    }}
                    active={activeProducerLocation === producer.slug && activePositionIndex === index}
                    popup={popupProducerSlug === producer.slug}
                  />
                </AdvancedMarker>
              )
            }

            return null
          })}
        </Map>
      </APIProvider>
    </div>
  )
}

export default NewProducerMap
